import React, { useState } from "react";
import type { EmailFormData } from "./EmailCard";
import { EmailCard } from "./EmailCard";
import { OtpCard } from "./OtpCard";
import { Alert, AlertDescription } from "./ui/alert";

export const AuthenticationFlow: React.FC = () => {
  const [step, setStep] = useState<"email" | "otp">("email");
  const [email, setEmail] = useState("");
  const [otp, setOtp] = useState("");
  const [error, setError] = useState<string | null>(null);
  const [isLoading, setIsLoading] = useState(false);

  const API_URL = import.meta.env.VITE_API_URL || "http://localhost:3001/api/v1";

  const handleEmailSubmit = async (data: EmailFormData) => {
    setIsLoading(true);
    setError(null);
    try {
      const response = await fetch(
        `${API_URL}/auth/send-otp`,
        {
          method: "POST",
          headers: { "Content-Type": "application/json" },
          body: JSON.stringify({ email: data.email }),
        }
      );
      const contentType = response.headers.get("content-type"); // Why are we getting content-type
      let responseData;

      if (contentType?.includes("application/json")) {
        responseData = await response.json();
      } else {
        // If response is not JSON (e.g. 404 Not Found from server crash/misconfig), read as text
        const text = await response.text();
        console.error("Non-JSON response:", text);
        let errorMessage = "Something went wrong. Please try again."

        if (response.status === 404) {
          errorMessage = "Service not reachable. Try again in Some time"
        } else if (response.status === 502) {
          errorMessage = "Service is temporarily unavailable. Please try again later."
        } else if (response.status === 500) {
          errorMessage = "Internal Server Error. We are working on it 👷"
        }

        throw new Error(errorMessage);
      }

      if (!response.ok) {
        throw new Error(responseData.error || "Failed to send OTP.");
      }
      setEmail(data.email);
      setStep("otp");
    } catch (err) {
      setError(
        err instanceof Error ? err.message : "An unknown error occurred."
      );
    } finally {
      setIsLoading(false);
    }
  };

  const handleOtpSubmit = async (otpValue: string) => {
    if (otpValue.length !== 6) {
      setError("Please enter a valid 6-digit code.");
      return;
    }
    setIsLoading(true);
    setError(null);
    try {
      const response = await fetch(
        `${API_URL}/auth/verify-otp`,
        {
          method: "POST",
          headers: { "Content-Type": "application/json" },
          body: JSON.stringify({ email, otp: otpValue }),
        }
      );
      const contentType = response.headers.get("content-type");
      let responseData;

      if (contentType?.includes("application/json")) {
        responseData = await response.json();
      } else {
        const text = await response.text();
        console.error("Non-JSON response:", text);

        let errorMessage = "Something went wrong. Please try again.";
        if (response.status === 404) {
          errorMessage = "Service not reachable. Try again in some time";
        } else if (response.status === 502 || response.status === 503) {
          errorMessage = "Service is temporarily unavailable. Please try again later.";
        } else if (response.status === 500) {
          errorMessage = "Internal Server Error. We are working on it.";
        }
        throw new Error(errorMessage);
      }

      if (!response.ok) {
        throw new Error(responseData.error || "Failed to verify OTP.");
      }
      globalThis.location.href = responseData.redirectUrl;
    } catch (err) {
      setError(
        err instanceof Error ? err.message : "An unknown error occurred."
      );
      setIsLoading(false);
    }
  };

  const handleBackToEmail = () => {
    setError(null);
    setOtp("");
    setStep("email");
  };

  return (
    <div className="w-full">
      <div className="mt-6 space-y-6">
        {/* Only show error at top level for email step */}
        {error && step === "email" && (
          <Alert variant="destructive" aria-live="assertive" className="max-w-md mx-auto">
            <AlertDescription>{error}</AlertDescription>
          </Alert>
        )}

        {step === "email" ? (
          <EmailCard
            onEmailSubmit={handleEmailSubmit}
            isLoading={isLoading}
          />
        ) : (
          <OtpCard
            email={email}
            otp={otp}
            setOtp={setOtp}
            onVerify={handleOtpSubmit}
            onBack={handleBackToEmail}
            error={error}
            loading={isLoading}
            // Pass mock resend for now or implement real one if needed
            onResend={async () => {
              // Re-use email submit logic for resend
              await handleEmailSubmit({ email });
              return { success: true, message: "OTP resent successfully" };
            }}
          />
        )}
      </div>
    </div>
  );
};
